<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { downloadCsvFile } from 'dashboard/helper/downloadHelper';
import TeletelCsatReviewNotesAPI from 'dashboard/api/teletel/csatReviewNotes';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

// This endpoint exports rather than lists: the controller answers with a CSV
// body, so the page is a date range and a download, not a table.
const { t } = useI18n();

const since = ref('');
const until = ref('');
const downloading = ref(false);

// The controller reads epoch seconds; a date input gives YYYY-MM-DD.
const toEpoch = (value, endOfDay = false) => {
  if (!value) return null;
  const date = new Date(`${value}T${endOfDay ? '23:59:59' : '00:00:00'}`);
  return Number.isNaN(date.getTime())
    ? null
    : Math.floor(date.getTime() / 1000);
};

const rangeInvalid = computed(() => {
  if (!since.value || !until.value) return false;
  return since.value > until.value;
});

const clearRange = () => {
  since.value = '';
  until.value = '';
};

const download = async () => {
  if (rangeInvalid.value) return;
  downloading.value = true;
  try {
    const { data } = await TeletelCsatReviewNotesAPI.download({
      since: toEpoch(since.value),
      until: toEpoch(until.value, true),
    });
    // A header row with no data rows means the range held no reviewed
    // responses. Saying so beats handing over a file that looks broken.
    const rows = String(data).trim().split('\n');
    if (rows.length <= 1) {
      useAlert(t('TELETEL_CSAT_NOTES.EMPTY_RESULT'));
      return;
    }
    downloadCsvFile(t('TELETEL_CSAT_NOTES.FILE_NAME'), data);
    useAlert(t('TELETEL_CSAT_NOTES.DOWNLOADED', { count: rows.length - 1 }));
  } catch (error) {
    useAlert(
      error?.response?.data?.error || t('TELETEL_CSAT_NOTES.DOWNLOAD_ERROR')
    );
  } finally {
    downloading.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col w-full gap-4">
    <div>
      <h1 class="text-lg font-medium text-n-slate-12">
        {{ t('TELETEL_CSAT_NOTES.TITLE') }}
      </h1>
      <p class="mt-1 mb-0 text-sm text-n-slate-11 max-w-prose">
        {{ t('TELETEL_CSAT_NOTES.DESCRIPTION') }}
      </p>
    </div>

    <div class="flex flex-col gap-4 p-4 border rounded-xl border-n-weak">
      <div class="flex flex-wrap items-end gap-4">
        <label class="flex flex-col gap-1 text-sm text-n-slate-11">
          {{ t('TELETEL_CSAT_NOTES.FROM') }}
          <input v-model="since" type="date" class="mb-0 w-44 text-sm" />
        </label>
        <label class="flex flex-col gap-1 text-sm text-n-slate-11">
          {{ t('TELETEL_CSAT_NOTES.TO') }}
          <input v-model="until" type="date" class="mb-0 w-44 text-sm" />
        </label>
        <NextButton v-if="since || until" slate ghost sm @click="clearRange">
          {{ t('TELETEL_CSAT_NOTES.CLEAR') }}
        </NextButton>
      </div>

      <p v-if="rangeInvalid" class="mb-0 text-sm text-n-ruby-11">
        {{ t('TELETEL_CSAT_NOTES.RANGE_INVALID') }}
      </p>
      <p v-else class="mb-0 text-xs text-n-slate-10">
        {{ t('TELETEL_CSAT_NOTES.RANGE_HINT') }}
      </p>

      <div>
        <NextButton
          blue
          sm
          :is-loading="downloading"
          :disabled="downloading || rangeInvalid"
          @click="download"
        >
          <Icon icon="i-lucide-download" class="size-4" />
          {{ t('TELETEL_CSAT_NOTES.DOWNLOAD') }}
        </NextButton>
      </div>
    </div>

    <p class="mb-0 text-xs text-n-slate-10 max-w-prose">
      {{ t('TELETEL_CSAT_NOTES.COLUMNS_HINT') }}
    </p>
  </div>
</template>
